/**
 * 재계약(연속 계약) 판정 훅
 *
 * 계약관리 : 계약 = 1:N 구조에서 파생되는 판정을 한 곳에 모은다.
 * - 연관 계약 테이블 / 재계약 버튼 (계약 정보 상세)
 * - 해지접수 / 해지완료 차단 가드 (계약 상태 변경)
 * 두 화면이 같은 규칙을 써야 하므로 순수 함수로 분리하고, 훅은 얇게 감싸기만 한다.
 */

import moment from "moment";
import { useMemo } from "react";
import { ContractStep, DetailTotalData, SiblingContract } from "src/api/contract/contract-types";
import { YmdFormat } from "src/utils";

/**
 * 계약관리 하위에서 '자리를 차지하지 않는' 계약 상태
 * - 신청취소 : 취소된 재계약이므로 후속 계약으로 보지 않는다
 * - 이용완료 / 해지완료 : 이미 끝난 계약
 */
const INVALID_CONTRACT_STEPS: Array<string> = [ContractStep.APPLY_CANCEL, ContractStep.USE_COMPLETE, ContractStep.TERMINATE_COMPLETE];

/** 재계약을 등록할 수 있는 원계약의 상태 */
const RENEWABLE_CONTRACT_STEPS: Array<string> = [ContractStep.USE_PROGRESS, ContractStep.TERMINATE_RECEIVED];

/** 재계약 기능을 제공하지 않는 공간상품 타입 (공간예약 / 관리비) */
const NON_RENEWABLE_PRODUCT_TYPES: Array<string> = ["TIME_COURT", "MAINTENANCE_FEE"];

/** 계약관리 하위에서 유효한(자리를 차지하는) 계약인지 */
export const isValidContract = (contract?: SiblingContract) => !INVALID_CONTRACT_STEPS.includes(String(contract?.contractStep));

/**
 * 재계약(원계약을 이어받아 생성된 계약)인지 판정한다.
 *
 * 원계약은 계약관리 하위에서 계약 id가 가장 작은 계약이다.
 * (RCA18 응답의 contractStep / contractId도 항상 원계약을 가리킨다)
 * 따라서 자기보다 작은 계약 id가 하나라도 있으면 재계약이다.
 * 신청취소된 계약도 '먼저 만들어진 계약'이므로 제외하지 않는다.
 *
 * contracts가 비어 있으면 원계약으로 본다(fail-open).
 * 조회 실패나 BE 미배포로 리스트가 비었을 때 원계약의 이용기간 수정까지 막히는 쪽이 더 위험하고,
 * 재계약의 계약시작일 변경은 BE(계약 수정 API)가 한 번 더 검사한다.
 */
export const isRenewalContract = (contracts?: Array<SiblingContract>, currentContractId?: SiblingContract["contractId"]) => {
  if (!contracts || contracts.length < 2) return false;
  return contracts.some((contract) => Number(contract.contractId) < Number(currentContractId));
};

/** 재계약 기능(연관 계약 테이블 + 재계약 버튼) 노출 대상 상품인지 */
export const isRenewableProductType = (spaceProductType?: string) => !NON_RENEWABLE_PRODUCT_TYPES.includes(String(spaceProductType));

const isSameContract = (a?: SiblingContract["contractId"], b?: SiblingContract["contractId"]) => String(a) === String(b);

/**
 * 계약관리 하위 계약 중 화면에서 대표로 보여줄 계약을 고른다.
 *
 * '이용중' 계약을 우선 쓰고, 없으면 최신 계약(계약 id가 가장 큰 계약)을 쓴다.
 * 재계약이 생기면 같은 계약관리에 계약이 여러 건 존재하므로,
 * 리스트에서 임의의 첫 항목을 집으면 종료된 원계약으로 연결될 수 있다.
 */
export const findRepresentativeContract = <T extends Pick<SiblingContract, "contractId" | "contractStep">>(contracts?: Array<T>) => {
  if (!contracts || contracts.length === 0) return undefined;

  return (
    contracts.find((contract) => String(contract.contractStep) === ContractStep.USE_PROGRESS) ??
    [...contracts].sort((a, b) => Number(b.contractId) - Number(a.contractId))[0]
  );
};

/**
 * 해지접수 / 해지완료를 막아야 하는지 판정한다.
 *
 * 처리하려는 계약 외에 유효한 계약이 하나라도 남아 있으면 연속 계약이 끊기므로 막는다.
 * BE(RCA14)도 같은 기준으로 eCT109를 반환하므로 판정식을 일부러 BE와 맞췄다.
 *
 * contracts가 비어 있으면 막지 않는다(fail-open). BE가 eCT109로 다시 검사하므로,
 * 조회 실패나 BE 미배포로 리스트가 비었을 때 정상 해지까지 막히는 쪽이 더 위험하다.
 */
export const hasBlockingSibling = (contracts?: Array<SiblingContract>, currentContractId?: SiblingContract["contractId"]) => {
  if (!contracts || contracts.length < 2) return false;
  return contracts.some((contract) => !isSameContract(contract.contractId, currentContractId) && isValidContract(contract));
};

/**
 * 재계약 등록 가능 여부
 *
 * 1) 공간예약 / 관리비 상품 제외
 * 2) 현재 계약이 이용중 또는 해지접수
 * 3) 현재 계약이 '유효한 마지막 계약' — 뒤에 유효한 계약이 남아 있으면 불가
 *
 * 3)은 BE(RCA01)가 검사하지 않는다. BE는 원계약 상태와 시작일만 보기 때문에,
 * 이미 재계약이 달린 원계약에서 한 번 더 등록하면 같은 기간의 계약이 중복 생성된다.
 * contracts가 비어 있으면 마지막 계약인지 확인할 수 없으므로 막는다(fail-closed).
 */
export const canRenewContract = (detailData?: DetailTotalData) => {
  const contract = detailData?.contract;
  const contracts = detailData?.contracts;

  if (!isRenewableProductType(detailData?.contractManage?.spaceProductType)) return false;
  if (!RENEWABLE_CONTRACT_STEPS.includes(String(contract?.contractStep))) return false;
  if (!contracts || contracts.length === 0) return false;

  return !hasBlockingSibling(contracts, contract?.contractId);
};

/**
 * 재계약 계약 시작일 = 원계약 종료일 + 1일, 00:00:00.000 (KST 자정)
 *
 * 원계약 종료일시는 23:59:59.999로 저장되므로 +1일 후 자정으로 내리면
 * BE가 검사하는 '원계약 종료일시 + 1ms'와 같은 시각이 된다.
 */
export const getRenewalStartTime = (originContractEndTime?: string) =>
  moment(originContractEndTime).add(1, "day").startOf("day").format(YmdFormat.WITH_TIME_ZONE_MILLIS);

/** 재계약 계약 종료일 = 선택한 날짜의 23:59:59.999 */
export const getRenewalEndTime = (selectedDate?: string | Date) =>
  moment(moment(selectedDate).format(YmdFormat.YYYY_MM_DD) + " 23:59:59.999").format(YmdFormat.WITH_TIME_ZONE_MILLIS);

/**
 * 등록하려는 기간이 기존 유효 계약과 겹치는지 검사한다.
 *
 * 시작일이 원계약 종료일 다음날로 고정이라 정상 흐름에서는 겹칠 수 없지만,
 * BE에 기간 중첩 검사가 없으므로 생성 직전 마지막 방어선으로 둔다.
 */
export const hasPeriodOverlap = (
  startTime?: string,
  endTime?: string,
  contracts?: Array<SiblingContract>,
  excludeContractId?: SiblingContract["contractId"],
) => {
  if (!contracts || contracts.length === 0) return false;

  const start = moment(startTime);
  const end = moment(endTime);

  return contracts
    .filter((contract) => isValidContract(contract) && !isSameContract(contract.contractId, excludeContractId))
    .some((contract) => start.isSameOrBefore(moment(contract.contractEndTime)) && end.isSameOrAfter(moment(contract.contractStartTime)));
};

/**
 * 계약 상세 화면용 재계약 판정 훅
 * @param detailData 계약 상세 조회 정보
 */
const useContractRenewal = (detailData?: DetailTotalData) => {
  // 계약시작일 오름차순. useContractApi에서 정렬해 내려주지만 방어적으로 한 번 더 보장한다
  const siblingContracts = useMemo(
    () => [...(detailData?.contracts || [])].sort((a, b) => moment(a.contractStartTime).valueOf() - moment(b.contractStartTime).valueOf()),
    [detailData?.contracts],
  );

  /** 연관 계약 테이블 / 재계약 버튼 노출 여부 */
  const isRenewalVisible = useMemo(
    () => isRenewableProductType(detailData?.contractManage?.spaceProductType),
    [detailData?.contractManage?.spaceProductType],
  );

  /** 재계약 버튼 활성화 여부 */
  const isRenewable = useMemo(() => canRenewContract(detailData), [detailData]);

  /** 해지접수 / 해지완료 차단 여부 */
  const isTerminateBlocked = useMemo(
    () => hasBlockingSibling(siblingContracts, detailData?.contract?.contractId),
    [siblingContracts, detailData?.contract?.contractId],
  );

  /** 현재 보고 있는 계약이 재계약인지 (계약시작일 수정 불가 대상) */
  const isRenewal = useMemo(
    () => isRenewalContract(siblingContracts, detailData?.contract?.contractId),
    [siblingContracts, detailData?.contract?.contractId],
  );

  /** 재계약의 계약 시작일 (원계약 종료일 다음날 자정) */
  const renewalStartTime = useMemo(() => getRenewalStartTime(detailData?.contract?.contractEndTime), [detailData?.contract?.contractEndTime]);

  return {
    siblingContracts,
    isRenewalVisible,
    isRenewable,
    isRenewal,
    isTerminateBlocked,
    renewalStartTime,
  };
};

export default useContractRenewal;
